import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  CONTENT_LIMITS,
  CTA_TYPES,
  CTA_TYPE_LABELS,
  PROMOTION_IMAGE_MAX_BYTES,
  PROMOTION_IMAGE_MIME_TYPES,
  canPerform,
  type CtaType,
  type TargetScope,
} from "@vital/shared";
import { PromotionPreview } from "../components/PromotionPreview";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { MetricCard } from "../components/MetricCard";
import { InspectPanel } from "../components/InspectPanel";
import { ContentDiffPanel } from "../components/ContentDiffPanel";
import { PasswordConfirmModal } from "../components/PasswordConfirmModal";
import { TargetFields, targetingPayload, formatTargetLabel } from "../components/TargetFields";
import {
  ScheduleFields,
  schedulePayloadFromLocal,
  localScheduleFromIso,
  formatKabulDisplay,
} from "../components/ScheduleFields";
import {
  PromotionInspectSummary,
  PromotionsFilterBar,
  PromotionsInventoryList,
} from "../components/promotions-inventory";
import { OpsEmptyState, SectionHeader } from "../components/ops-display";
import {
  ComposerHeader,
  ComposerPanel,
  ComposerSection,
  ComposerWorkspace,
} from "../components/ComposerChrome";
import { StickyFormActions } from "../components/StickyFormActions";
import { cmsStrings } from "../terminology";
import { useAuth } from "../context/AuthContext";
import { api, ApiError, type ContentDraftDiff } from "../lib/api";
import { useInspectSearchParam } from "../lib/use-inspect-search-param";
import { FILTER_ALL } from "../lib/content-state";
import {
  filterAndSortPromotions,
  promotionBadgeOptions,
  promotionCtaLabelFa,
  type PromotionSortKey,
} from "../lib/promotions-ops";

type Site = { id: string; code: string; name: string };
type SiteGroup = {
  id: string;
  name: string;
  members: Array<{ siteId: string; siteCode: string; siteName: string }>;
};
type Promotion = {
  id: string;
  state: string;
  scope: string;
  title: string;
  body: string;
  badge: string;
  ctaType: CtaType | null;
  ctaLabel: string;
  ctaHref: string | null;
  imagePath: string | null;
  imageAlt: string;
  priority: number;
  rejectionReason: string | null;
  submittedAt: string | null;
  pausedFromState: string | null;
  siteCode: string | null;
  siteName: string | null;
  groupId: string | null;
  groupName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
};

type Snapshot = {
  id: string;
  version: number;
  publishedAt: string;
  payloadChecksum: string;
  publishedPayload: {
    title: string;
    ctaHref: string | null;
    ctaLabel: string;
  };
};

type PasswordMode = "publish" | "archive" | "pause" | "unpause";

export function PromotionsPage() {
  const { user } = useAuth();
  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role ?? null,
  };
  const canEdit = canPerform({ ...authInput, permission: "content.edit" });
  const canPublish = canPerform({ ...authInput, permission: "content.publish" });

  const [sites, setSites] = useState<Site[]>([]);
  const [groups, setGroups] = useState<SiteGroup[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [approvedUrls, setApprovedUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState<string>(FILTER_ALL);
  const [listQuery, setListQuery] = useState("");
  const [listSort, setListSort] = useState<PromotionSortKey>("updated");
  const [selectedId, setSelectedId] = useInspectSearchParam();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [badge, setBadge] = useState("");
  const [siteCode, setSiteCode] = useState("herat-firdoosi-01");
  const [targetScope, setTargetScope] = useState<TargetScope>("site");
  const [groupId, setGroupId] = useState("");
  const [priority, setPriority] = useState(0);
  const [ctaType, setCtaType] = useState<CtaType | "">("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [customApprovedUrl, setCustomApprovedUrl] = useState("");
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [imageAlt, setImageAlt] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [endsAtLocal, setEndsAtLocal] = useState("");
  const [saving, setSaving] = useState(false);

  const [passwordModal, setPasswordModal] = useState<{
    mode: PasswordMode;
    promotion: Promotion;
  } | null>(null);
  const [password, setPassword] = useState("");
  const [actionPending, setActionPending] = useState(false);

  const [rejectModal, setRejectModal] = useState<Promotion | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const [diffModal, setDiffModal] = useState<{
    promotion: Promotion;
    diff: ContentDraftDiff | null;
    loading: boolean;
  } | null>(null);

  const [historyTarget, setHistoryTarget] = useState<Promotion | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setBody("");
    setBadge("");
    setCtaType("");
    setCtaLabel("");
    setCustomApprovedUrl("");
    setImagePath(null);
    setImageAlt("");
    setImagePreviewUrl(null);
    setSiteCode("herat-firdoosi-01");
    setTargetScope("site");
    setGroupId("");
    setPriority(0);
    setStartsAtLocal("");
    setEndsAtLocal("");
  };

  const loadFormFromPromotion = (p: Promotion) => {
    setEditingId(p.id);
    setTitle(p.title);
    setBody(p.body);
    setBadge(p.badge);
    setSiteCode(p.siteCode ?? "herat-firdoosi-01");
    setTargetScope((p.scope as TargetScope) ?? "site");
    setGroupId(p.groupId ?? "");
    setPriority(p.priority ?? 0);
    setCtaType(p.ctaType ?? "");
    setCtaLabel(p.ctaLabel);
    setCustomApprovedUrl(p.ctaType === "custom_https" ? (p.ctaHref ?? "") : "");
    setImagePath(p.imagePath);
    setImageAlt(p.imageAlt ?? "");
    setImagePreviewUrl(p.imagePath);
    const schedule = localScheduleFromIso(p.startsAt, p.endsAt);
    setStartsAtLocal(schedule.startsAtLocal);
    setEndsAtLocal(schedule.endsAtLocal);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [siteList, groupList, list, urls] = await Promise.all([
        api.listSites(),
        api.listSiteGroups(),
        api.listPromotions(),
        api.listApprovedCtaUrls(),
      ]);
      setSites(siteList);
      setGroups(groupList);
      setPromotions(list as Promotion[]);
      setApprovedUrls(urls);
      setSiteCode((current) =>
        siteList.length > 0 && !siteList.some((s) => s.code === current)
          ? siteList[0]!.code
          : current,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  async function handleImageSelect(file: File | null) {
    if (!file || !canEdit) return;
    setUploadingImage(true);
    setError(null);
    try {
      const result = await api.uploadPromotionImage(file);
      setImagePath(result.path);
      setImagePreviewUrl(result.path);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری تصویر ناموفق");
    } finally {
      setUploadingImage(false);
    }
  }

  function clearImage() {
    setImagePath(null);
    setImageAlt("");
    setImagePreviewUrl(null);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    const payload = {
      title,
      body,
      badge,
      ...targetingPayload(targetScope, siteCode, groupId, priority),
      ctaType: ctaType || null,
      ctaLabel,
      customApprovedUrl: ctaType === "custom_https" ? customApprovedUrl : undefined,
      imagePath,
      imageAlt,
      ...schedulePayloadFromLocal(startsAtLocal, endsAtLocal),
    };

    try {
      if (editingId) {
        await api.updatePromotion(editingId, payload);
        setSuccess(cmsStrings.promotionsComposer.draftUpdated);
      } else {
        await api.createPromotion(payload);
        setSuccess(cmsStrings.promotionsComposer.draftSaved);
        resetForm();
      }
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ذخیره ناموفق");
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordAction(e: FormEvent) {
    e.preventDefault();
    if (!passwordModal) return;
    setActionPending(true);
    setError(null);
    try {
      const { mode, promotion: p } = passwordModal;
      if (mode === "publish") {
        await api.publishPromotion(p.id, password);
        setSuccess(
          p.startsAt ? `«${p.title}» زمان‌بندی شد.` : `«${p.title}» منتشر شد.`,
        );
        resetForm();
      } else if (mode === "archive") {
        await api.archivePromotion(p.id, password);
        setSuccess(`«${p.title}» بایگانی شد.`);
      } else if (mode === "pause") {
        await api.pausePromotion(p.id, password);
        setSuccess(`«${p.title}» متوقف شد.`);
      } else {
        await api.unpausePromotion(p.id, password);
        setSuccess(`«${p.title}» از توقف خارج شد.`);
      }
      setPasswordModal(null);
      setPassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "عملیات ناموفق");
    } finally {
      setActionPending(false);
    }
  }

  async function handleSubmit(p: Promotion) {
    setError(null);
    try {
      await api.submitPromotion(p.id);
      setSuccess(`«${p.title}» برای تأیید ارسال شد.`);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ارسال برای تأیید ناموفق");
    }
  }

  async function handleReject(e: FormEvent) {
    e.preventDefault();
    if (!rejectModal) return;
    setRejecting(true);
    setError(null);
    try {
      await api.rejectPromotion(rejectModal.id, rejectReason.trim());
      setSuccess(`«${rejectModal.title}» رد شد.`);
      setRejectModal(null);
      setRejectReason("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "رد کردن ناموفق");
    } finally {
      setRejecting(false);
    }
  }

  async function openDiff(p: Promotion) {
    setDiffModal({ promotion: p, diff: null, loading: true });
    try {
      const diff = await api.getPromotionDiff(p.id);
      setDiffModal({ promotion: p, diff, loading: false });
    } catch (err) {
      setDiffModal(null);
      setError(err instanceof ApiError ? err.message : "مقایسه ناموفق");
    }
  }

  async function openHistory(p: Promotion) {
    setHistoryTarget(p);
    setHistoryLoading(true);
    try {
      const rows = await api.listPromotionSnapshots(p.id);
      setSnapshots(rows as Snapshot[]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری تاریخچه ناموفق");
      setHistoryTarget(null);
    } finally {
      setHistoryLoading(false);
    }
  }

  const filtered = useMemo(
    () =>
      filterAndSortPromotions(promotions, {
        query: listQuery,
        listFilter,
        sort: listSort,
      }),
    [promotions, listFilter, listQuery, listSort],
  );
  const queueCount = promotions.filter((p) => p.state === "pending_approval").length;
  const pausedCount = promotions.filter((p) => p.state === "paused").length;
  const publishedCount = promotions.filter(
    (p) => p.state === "published" || p.state === "scheduled",
  ).length;

  const selectedPromotion = useMemo(
    () => promotions.find((p) => p.id === selectedId) ?? null,
    [promotions, selectedId],
  );

  useEffect(() => {
    if (loading) return;
    if (selectedId && !promotions.some((p) => p.id === selectedId)) {
      setSelectedId(null);
    }
  }, [loading, promotions, selectedId, setSelectedId]);

  useEffect(() => {
    if (!selectedId) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (passwordModal || rejectModal || diffModal || historyTarget) return;
      e.preventDefault();
      setSelectedId(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedId, passwordModal, rejectModal, diffModal, historyTarget]);

  useEffect(() => {
    if (!editingId) return;
    document.getElementById("promotion-composer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editingId]);

  const badgeOptions = useMemo(() => promotionBadgeOptions(badge), [badge]);
  const c = cmsStrings.promotionsComposer;
  const passwordTitles: Record<PasswordMode, string> = {
    publish: c.publishConfirmTitle,
    archive: c.archiveConfirmTitle,
    pause: c.pauseConfirmTitle,
    unpause: c.resumeConfirmTitle,
  };
  const passwordLabels: Record<PasswordMode, string> = {
    publish: c.publishConfirmLabel,
    archive: c.archiveConfirmLabel,
    pause: c.pauseConfirmLabel,
    unpause: c.resumeConfirmLabel,
  };
  const passwordImpacts: Record<PasswordMode, string> = {
    publish: c.publishImpact,
    archive: c.archiveImpact,
    pause: c.pauseImpact,
    unpause: c.resumeImpact,
  };
  const imageMaxKb = Math.round(PROMOTION_IMAGE_MAX_BYTES / 1024);
  const imageAccept = PROMOTION_IMAGE_MIME_TYPES.join(",");

  function renderPromotionInspectFooter(p: Promotion) {
    const tools: ReactNode[] = [];
    const primary: ReactNode[] = [];

    tools.push(
      <button key="compare" type="button" className="btn btn--ghost btn--sm" onClick={() => void openDiff(p)}>
        {c.compare}
      </button>,
    );

    if (p.state === "draft") {
      tools.push(
        <button
          key="edit"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setSelectedId(p.id);
            loadFormFromPromotion(p);
          }}
        >
          {c.edit}
        </button>,
      );
      if (canEdit) {
        tools.push(
          <button
            key="submit"
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void handleSubmit(p)}
          >
            {c.submitForApproval}
          </button>,
        );
      }
      if (canPublish) {
        primary.push(
          <button
            key="publish"
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "publish", promotion: p });
              setPassword("");
            }}
          >
            {c.publish}
          </button>,
        );
      }
    } else if (p.state === "pending_approval" && canPublish) {
      tools.push(
        <button
          key="reject"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setRejectModal(p);
            setRejectReason("");
          }}
        >
          {c.reject}
        </button>,
      );
      primary.push(
        <button
          key="approve"
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => {
            setPasswordModal({ mode: "publish", promotion: p });
            setPassword("");
          }}
        >
          {c.approveAndPublish}
        </button>,
      );
    } else if (p.state === "published" || p.state === "scheduled") {
      tools.push(
        <button key="history" type="button" className="btn btn--ghost btn--sm" onClick={() => void openHistory(p)}>
          {c.history}
        </button>,
      );
      if (canPublish) {
        tools.push(
          <button
            key="pause"
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "pause", promotion: p });
              setPassword("");
            }}
          >
            {c.pause}
          </button>,
          <button
            key="archive"
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "archive", promotion: p });
              setPassword("");
            }}
          >
            {c.archive}
          </button>,
        );
      }
    } else if (p.state === "paused" && canPublish) {
      primary.push(
        <button
          key="resume"
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => {
            setPasswordModal({ mode: "unpause", promotion: p });
            setPassword("");
          }}
        >
          {c.resume}
        </button>,
      );
    } else if (p.state === "archived") {
      tools.push(
        <button key="history" type="button" className="btn btn--ghost btn--sm" onClick={() => void openHistory(p)}>
          {c.history}
        </button>,
      );
    }

    return (
      <div className="inspect-panel__footer-inner">
        {tools.length > 0 ? <div className="inspect-panel__footer-tools">{tools}</div> : null}
        {primary.length > 0 ? <div className="inspect-panel__footer-primary">{primary}</div> : null}
      </div>
    );
  }

  function renderPromotionActions(p: Promotion) {
    return (
      <>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void openDiff(p)}>
          {c.compare}
        </button>
        {p.state === "draft" && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setSelectedId(p.id);
                loadFormFromPromotion(p);
              }}
            >
              {c.edit}
            </button>
            {canEdit && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void handleSubmit(p)}
              >
                {c.submitForApproval}
              </button>
            )}
            {canPublish && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => {
                  setPasswordModal({ mode: "publish", promotion: p });
                  setPassword("");
                }}
              >
                {c.publish}
              </button>
            )}
          </>
        )}
        {p.state === "pending_approval" && canPublish && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setRejectModal(p);
                setRejectReason("");
              }}
            >
              {c.reject}
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => {
                setPasswordModal({ mode: "publish", promotion: p });
                setPassword("");
              }}
            >
              {c.approveAndPublish}
            </button>
          </>
        )}
        {(p.state === "published" || p.state === "scheduled") && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => void openHistory(p)}
            >
              {c.history}
            </button>
            {canPublish && (
              <>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setPasswordModal({ mode: "pause", promotion: p });
                    setPassword("");
                  }}
                >
                  {c.pause}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setPasswordModal({ mode: "archive", promotion: p });
                    setPassword("");
                  }}
                >
                  {c.archive}
                </button>
              </>
            )}
          </>
        )}
        {p.state === "paused" && canPublish && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "unpause", promotion: p });
              setPassword("");
            }}
          >
            {c.resume}
          </button>
        )}
        {p.state === "archived" && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void openHistory(p)}
          >
            {c.history}
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={cmsStrings.nav.promotions}
        actions={
          canEdit ? (
            <a href="#promotion-composer" className="btn btn--primary btn--sm">
              {cmsStrings.promotionsComposer.createDraftCta}
            </a>
          ) : undefined
        }
      />

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}

      <div className="metric-strip" data-vt-inventory-metrics aria-label={cmsStrings.promotionsUi.listTitle}>
        <MetricCard
          label={cmsStrings.inventory.filterAll}
          value={promotions.length}
          loading={loading}
        />
        <MetricCard
          label={cmsStrings.inventory.filterQueue}
          value={queueCount}
          loading={loading}
          tone={queueCount > 0 ? "warning" : "neutral"}
        />
        <MetricCard
          label={cmsStrings.overviewUi.publishedOrScheduled}
          value={publishedCount}
          loading={loading}
          sublabel={
            pausedCount > 0
              ? `${cmsStrings.inventory.filterPaused}: ${pausedCount}`
              : undefined
          }
        />
      </div>

      <div className="page-stack page-stack--inventory">
      <div
        className={`content-ops-layout${selectedPromotion ? " content-ops-layout--inspect-open" : ""}`}
        data-vt-content-ops
        data-vt-inspect-open={selectedPromotion ? "true" : "false"}
      >
        <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
          <SectionHeader title={cmsStrings.promotionsUi.listTitle} />
          <PromotionsFilterBar
            listFilter={listFilter}
            onListFilterChange={setListFilter}
            queueCount={queueCount}
            query={listQuery}
            onQueryChange={setListQuery}
            sort={listSort}
            onSortChange={setListSort}
          />
          {loading ? (
            <div className="inventory-skeleton" aria-busy="true" aria-label={cmsStrings.shell.loading}>
              <div className="inventory-skeleton__row" style={{ width: "92%" }} />
              <div className="inventory-skeleton__row" style={{ width: "78%" }} />
              <div className="inventory-skeleton__row" style={{ width: "85%" }} />
              <div className="inventory-skeleton__row" style={{ width: "70%" }} />
              <div className="inventory-skeleton__row" style={{ width: "88%" }} />
            </div>
          ) : promotions.length === 0 ? (
            <OpsEmptyState title={cmsStrings.promotionsUi.emptyTitle} />
          ) : filtered.length === 0 ? (
            <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
          ) : (
            <PromotionsInventoryList
              rows={filtered}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
              renderActions={(row) => renderPromotionActions(row as Promotion)}
            />
          )}
        </Card>

        {selectedPromotion && (
          <InspectPanel
            title={cmsStrings.promotionsUi.inspectTitle}
            className="content-inspect-panel"
            onClose={() => setSelectedId(null)}
            closeLabel={c.close}
            footer={renderPromotionInspectFooter(selectedPromotion)}
          >
            <PromotionInspectSummary item={selectedPromotion} />
          </InspectPanel>
        )}
      </div>

      {canEdit && (
      <ComposerWorkspace id="promotion-composer">
        <ComposerPanel>
          <ComposerHeader
            kind={cmsStrings.composerChrome.kindPromotion}
            title={editingId ? c.cardTitleEdit : c.cardTitleCreate}
            statusLabel={
              editingId
                ? cmsStrings.composerChrome.statusEditDraft
                : cmsStrings.composerChrome.statusCreate
            }
            statusTone={editingId ? "warning" : "info"}
          />
          <form
            className="content-form content-form--in-card composer-form composer-form--promotion"
            onSubmit={handleSave}
          >
            <ComposerSection id="promotion-section-basic" title={cmsStrings.composerChrome.sectionBasic} bare>
              <div className="composer-fields-row composer-fields-row--title-badge">
                <label className="composer-field--grow">
                  <span className="composer-label">
                    {c.titleLabel}
                    <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                      {title.length}/{CONTENT_LIMITS.titleMax}
                    </bdi>
                  </span>
                  <input
                    className="composer-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, CONTENT_LIMITS.titleMax))}
                    required
                    maxLength={CONTENT_LIMITS.titleMax}
                  />
                </label>
                <label className="composer-field--badge">
                  <span>{c.badgeLabel}</span>
                  <select
                    value={badge}
                    onChange={(e) => setBadge(e.target.value)}
                  >
                    <option value="">{c.badgeNone}</option>
                    {badgeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label>
                <span className="composer-label">
                  {c.bodyLabel}
                  <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                    {body.length}/{CONTENT_LIMITS.bodyMax}
                  </bdi>
                </span>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value.slice(0, CONTENT_LIMITS.bodyMax))}
                  required
                  maxLength={CONTENT_LIMITS.bodyMax}
                  rows={4}
                />
              </label>
            </ComposerSection>
            <ComposerSection
              id="promotion-section-cta"
              title={cmsStrings.composerChrome.sectionCtaImage}
              bare
            >
              <div className="composer-fields-row">
                <label>
                  <span>
                    {c.ctaDestination} — {c.optionalSuffix}
                  </span>
                  <select
                    value={ctaType}
                    onChange={(e) => {
                      const v = e.target.value as CtaType | "";
                      setCtaType(v);
                      if (!v) {
                        setCtaLabel("");
                        setCustomApprovedUrl("");
                      }
                    }}
                  >
                    <option value="">{c.ctaNone}</option>
                    {CTA_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {CTA_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
                {ctaType && (
                  <label>
                    <span className="composer-label">
                      {c.ctaButtonLabel}
                      <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                        {ctaLabel.length}/{CONTENT_LIMITS.ctaLabelMax}
                      </bdi>
                    </span>
                    <input
                      value={ctaLabel}
                      onChange={(e) =>
                        setCtaLabel(e.target.value.slice(0, CONTENT_LIMITS.ctaLabelMax))
                      }
                      required
                      maxLength={CONTENT_LIMITS.ctaLabelMax}
                    />
                  </label>
                )}
              </div>
              {ctaType === "custom_https" && (
                <label>
                  <span>{c.ctaApprovedUrl}</span>
                  <select
                    value={customApprovedUrl}
                    onChange={(e) => setCustomApprovedUrl(e.target.value)}
                    required
                    dir="ltr"
                    className="ltr-isolate"
                  >
                    <option value="">{c.ctaSelectUrl}</option>
                    {approvedUrls.map((url) => (
                      <option key={url} value={url}>
                        {url}
                      </option>
                    ))}
                  </select>
                  {approvedUrls.length === 0 && (
                    <p className="field-hint">{c.ctaAllowlistEmpty}</p>
                  )}
                </label>
              )}
              <div className="composer-image-block">
                {imagePreviewUrl ? (
                  <div className="composer-image-preview">
                    <img
                      src={imagePreviewUrl}
                      alt={imageAlt || title || c.previewEmptyTitle}
                      className="composer-image-preview__thumb"
                    />
                    <div className="composer-image-preview__meta">
                      <span className="composer-image-preview__name ltr-isolate" dir="ltr">
                        {imagePath?.split("/").pop() ?? c.imagePick}
                      </span>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={clearImage}>
                        {c.imageClear}
                      </button>
                    </div>
                  </div>
                ) : null}
                <label>
                  <span>
                    {c.imageLabelBefore}{" "}
                    <bdi dir="ltr" className="ltr-isolate latin-digits">
                      {imageMaxKb}
                    </bdi>{" "}
                    {c.imageLabelAfter}
                    {!imagePath ? ` — ${c.optionalSuffix}` : null}
                  </span>
                  <span className={`file-drop-hint${imagePreviewUrl ? " file-drop-hint--compact" : ""}`}>
                    {uploadingImage ? c.imageUploading : c.imagePick}
                    <input
                      type="file"
                      accept={imageAccept}
                      disabled={uploadingImage || !canEdit}
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        void handleImageSelect(file);
                        e.target.value = "";
                      }}
                    />
                  </span>
                </label>
                {imagePath ? (
                  <label>
                    <span className="composer-label">
                      {c.imageAltLabel}
                      <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                        {imageAlt.length}/{CONTENT_LIMITS.imageAltMax}
                      </bdi>
                    </span>
                    <input
                      value={imageAlt}
                      onChange={(e) =>
                        setImageAlt(e.target.value.slice(0, CONTENT_LIMITS.imageAltMax))
                      }
                      maxLength={CONTENT_LIMITS.imageAltMax}
                    />
                  </label>
                ) : null}
              </div>
            </ComposerSection>
            <ComposerSection
              id="promotion-section-targeting"
              title={cmsStrings.composerChrome.sectionTargeting}
              bare
            >
              <TargetFields
                compact
                sites={sites}
                groups={groups}
                targetScope={targetScope}
                siteCode={siteCode}
                groupId={groupId}
                priority={priority}
                onTargetScopeChange={setTargetScope}
                onSiteCodeChange={setSiteCode}
                onGroupIdChange={setGroupId}
                onPriorityChange={setPriority}
                labels={{
                  ...cmsStrings.contentTargeting,
                  globalHint: "",
                  groupEmptyHint: "",
                }}
              />
            </ComposerSection>
            <ComposerSection
              id="promotion-section-schedule"
              title={cmsStrings.composerChrome.sectionSchedule}
              bare
            >
              <ScheduleFields
                compact
                startsAtLocal={startsAtLocal}
                endsAtLocal={endsAtLocal}
                onStartsAtChange={setStartsAtLocal}
                onEndsAtChange={setEndsAtLocal}
                legend={c.scheduleLegend}
                hint={null}
              />
            </ComposerSection>
            <StickyFormActions>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? c.saving : editingId ? c.updateDraft : c.saveDraft}
              </button>
              {editingId && (
                <button type="button" className="btn btn--ghost" onClick={resetForm}>
                  {c.cancel}
                </button>
              )}
            </StickyFormActions>
          </form>
        </ComposerPanel>

        <Card
          title={c.previewTitle}
          className="preview-panel"
          bodyClassName="card__body--flush"
        >
          <PromotionPreview
            title={title}
            body={body}
            badge={badge}
            ctaLabel={ctaLabel}
            ctaType={ctaType || null}
            imageUrl={imagePreviewUrl}
            imageAlt={imageAlt}
          />
        </Card>
      </ComposerWorkspace>
      )}

      </div>

      {passwordModal && (
        <PasswordConfirmModal
          title={passwordTitles[passwordModal.mode]}
          description={passwordImpacts[passwordModal.mode]}
          summary={
            <dl className="publish-summary">
              <dt>{c.summaryTitle}</dt>
              <dd>{passwordModal.promotion.title}</dd>
              <dt>{c.summaryTarget}</dt>
              <dd>{formatTargetLabel(passwordModal.promotion)}</dd>
              <dt>{c.summarySchedule}</dt>
              <dd>
                <bdi dir="ltr" className="ltr-isolate">
                  {formatKabulDisplay(passwordModal.promotion.startsAt)} –{" "}
                  {formatKabulDisplay(passwordModal.promotion.endsAt)}
                </bdi>
              </dd>
              <dt>{c.summaryCta}</dt>
              <dd>{promotionCtaLabelFa(passwordModal.promotion.ctaType, passwordModal.promotion.ctaLabel)}</dd>
            </dl>
          }
          password={password}
          onPasswordChange={setPassword}
          confirming={actionPending}
          confirmLabel={passwordLabels[passwordModal.mode]}
          tone={
            passwordModal.mode === "archive" || passwordModal.mode === "pause"
              ? "danger"
              : "primary"
          }
          onCancel={() => setPasswordModal(null)}
          onConfirm={handlePasswordAction}
        />
      )}

      {rejectModal && (
        <div className="modal-backdrop" role="presentation" onClick={() => setRejectModal(null)}>
          <div className="modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{c.rejectDialogTitle}</h2>
            <p>
              «{rejectModal.title}» {c.rejectDialogBody}
            </p>
            <form onSubmit={handleReject}>
              <label>
                <span>{c.rejectReason}</span>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value.slice(0, 500))}
                  required
                  rows={3}
                  maxLength={500}
                />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setRejectModal(null)}>
                  {c.cancel}
                </button>
                <button type="submit" className="btn btn--danger" disabled={rejecting} aria-busy={rejecting}>
                  {rejecting ? c.rejecting : c.rejectConfirm}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {diffModal && (
        <div className="modal-backdrop" role="presentation" onClick={() => setDiffModal(null)}>
          <div className="modal modal--wide" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>
              {c.diffTitlePrefix} — {diffModal.promotion.title}
            </h2>
            {diffModal.loading || !diffModal.diff ? (
              <p className="card__empty">{c.loadingInline}</p>
            ) : (
              <ContentDiffPanel
                fields={diffModal.diff.fields}
                hasPublishedSnapshot={diffModal.diff.hasPublishedSnapshot}
                publishedVersion={diffModal.diff.publishedVersion}
                state={diffModal.diff.state}
              />
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn--ghost" onClick={() => setDiffModal(null)}>
                {c.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyTarget && (
        <div className="modal-backdrop" role="presentation" onClick={() => setHistoryTarget(null)}>
          <div className="modal modal--wide" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{c.historyTitle}</h2>
            <p>{historyTarget.title}</p>
            {historyLoading ? (
              <p>{c.loadingInline}</p>
            ) : snapshots.length === 0 ? (
              <p>{c.historyEmpty}</p>
            ) : (
              <ul className="snapshot-list">
                {snapshots.map((s) => (
                  <li key={s.id}>
                    <strong>
                      {c.historyVersion}{" "}
                      <bdi dir="ltr" className="ltr-isolate">
                        {s.version}
                      </bdi>
                    </strong>
                    <span className="ltr-isolate" dir="ltr">
                      {s.publishedAt}
                    </span>
                    <code className="ltr-isolate" dir="ltr">
                      {s.payloadChecksum.slice(0, 16)}…
                    </code>
                  </li>
                ))}
              </ul>
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn--ghost" onClick={() => setHistoryTarget(null)}>
                {c.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
